import { BrushMask, GradientMask, RadialMask } from "@/core/types"
import { MappedMask, getMaskIcon } from "@/store/editor/mask-utils"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import {
	mappedMasksAtom,
	maskDetectionCoordsVersionAtom,
	maskDetectionStateAtom,
	selectedMaskAtom,
	selectedMaskGroupAtom,
	showingMaskingToolsOverlayAtom,
} from "@/store/editor/masks-state"
import { RadialMaskControls } from "@/components/Masking/RadialMaskControls"
import { GradientMaskControls } from "@/components/Masking/GradientMaskControls"
import { BrushMaskControls } from "@/components/Masking/BrushMaskControls"
import { CanvasControlsOverlay } from "@/components/canvas-controls/CanvasControls"
import { showingMaskingPanelAtom } from "@/store/ui-views-state"
import { PropsWithChildren } from "react"
import { Pin } from "@/components/canvas-controls/Pin"
import { setMaskOverlay } from "@/store/editor/mask-overlays"
import { colorPickerSessionAtom } from "@/store/color-picker-state"
import { MainAIMaskResolver } from "@/io/assets/ai-mask/main-ai-mask-resolver"
import {
	getMaskIconOverlay,
	MaskIconOverlay,
	spreadOverlappingMaskIconOverlays,
} from "./MaskingToolsOverlayUtils"

export const MaskingToolsOverlay = () => {
	const isMaskingActive = useAtomValue(showingMaskingPanelAtom)
	const colorPickerSession = useAtomValue(colorPickerSessionAtom)
	const setSelectedMaskGroup = useSetAtom(selectedMaskGroupAtom)
	const [selectedMask, setSelectedMask] = useAtom(selectedMaskAtom)
	useAtomValue(maskDetectionStateAtom)
	useAtomValue(maskDetectionCoordsVersionAtom)
	const masks = useAtomValue(mappedMasksAtom)
	const maskDetectionCoords = MainAIMaskResolver.people.maskDetectionCoords

	const pins: MaskIconOverlay[] = []

	masks.forEach((group) => {
		group.children.forEach((mask) => {
			const isSelected = mask.properties.id === selectedMask?.properties.id
			const alwaysShowPin =
				mask.properties.type === "subject" ||
				mask.properties.type === "people" ||
				mask.properties.type === "background" ||
				mask.properties.type === "sky"

			if (isSelected && !alwaysShowPin) {
				return
			}

			const pin = getMaskIconOverlay(
				mask.properties,
				group.properties.id,
				maskDetectionCoords,
				isSelected,
			)
			if (pin) {
				pins.push(pin)
			}
		})
	})
	const visiblePins = spreadOverlappingMaskIconOverlays(pins)

	if (!isMaskingActive || colorPickerSession !== null) {
		return null
	}

	return (
		<ToolOverlay>
			{visiblePins.map((pin) => {
				return (
					<Pin
						name={getMaskIcon(pin.type)}
						key={pin.id}
						x={pin.x}
						y={pin.y}
						active={pin.selected}
						onPointerEnter={() => {
							setMaskOverlay(pin.id)
						}}
						onPointerLeave={() => {
							setMaskOverlay(null)
						}}
						onClick={() => {
							setSelectedMaskGroup(pin.groupId)
							setSelectedMask(pin.id)
						}}
						onPointerDown={(e) => {
							e.stopPropagation()
						}}
					/>
				)
			})}
		</ToolOverlay>
	)
}

const ToolOverlay = (props: PropsWithChildren) => {
	const selectedMask = useAtomValue(selectedMaskAtom)
	const showingOverlay = useAtomValue(showingMaskingToolsOverlayAtom)

	switch (selectedMask?.properties.type) {
		case "radial":
			return (
				<RadialMaskControls
					mask={selectedMask as MappedMask<RadialMask>}
					hidden={!showingOverlay}
				>
					{props.children}
				</RadialMaskControls>
			)

		case "gradient":
			return (
				<GradientMaskControls
					mask={selectedMask as MappedMask<GradientMask>}
					hidden={!showingOverlay}
				>
					{props.children}
				</GradientMaskControls>
			)

		case "brush":
			return (
				<BrushMaskControls
					mask={selectedMask as MappedMask<BrushMask>}
					hidden={!showingOverlay}
				>
					{props.children}
				</BrushMaskControls>
			)

		default:
			return (
				<CanvasControlsOverlay hidden={!showingOverlay}>
					{props.children}
				</CanvasControlsOverlay>
			)
	}
}
